import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { API } from '../App';
import { useCredits } from '../context/CreditContext';
import { useAuthModal } from '../context/AuthModalContext';

// ProtectedRoute now subscribes to CreditContext.isAuthenticated.
//
// Earlier versions kept their own local `isAuthenticated` state from a
// one-shot `/auth/me` call. When the user logged out via the navbar,
// CreditContext flipped `isAuthenticated` to false, but ProtectedRoute
// never noticed — so the protected page kept rendering. Now any sign-out
// anywhere in the app flows through CreditContext and the gate reacts
// instantly across every protected route.
//
// When a guest hits a protected route, instead of bouncing them to "/",
// we open the global AuthModal in place. The URL stays put so once they
// finish signing in the route auto-renders without a navigation step.
export default function ProtectedRoute({ children }) {
  const location = useLocation();
  const { isAuthenticated: contextAuth } = useCredits();
  const { openAuth, isOpen: authModalOpen } = useAuthModal();
  const [localAuth, setLocalAuth] = useState(
    location.state?.user ? true : null
  );

  useEffect(() => {
    if (location.state?.user) return;
    let cancelled = false;
    (async () => {
      try {
        await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (!cancelled) setLocalAuth(true);
      } catch {
        if (!cancelled) setLocalAuth(false);
      }
    })();
    return () => { cancelled = true; };
  }, [location.state]);

  const effectiveAuth = contextAuth ?? localAuth;

  // Auto-open the auth modal as soon as we know the visitor is a guest.
  // The route stays on its URL so finishing the sign-in flow renders the
  // protected page without an extra navigation. We pass the current path
  // as the post-login redirect so external `openAuth` callers behave the
  // same as the in-route fallback.
  useEffect(() => {
    if (effectiveAuth === false && !authModalOpen) {
      openAuth(location.pathname + location.search + location.hash);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveAuth]);

  if (effectiveAuth === null) {
    return <div className="min-h-screen bg-background" aria-busy="true" />;
  }

  if (!effectiveAuth) {
    // Render a friendly placeholder behind the modal so closing it doesn't
    // leave the user staring at a totally blank page.
    return (
      <div className="min-h-screen flex items-center justify-center px-4 pt-20">
        <div className="max-w-sm w-full text-center" data-testid="protected-route-gate">
          <div className="w-12 h-12 rounded-2xl bg-brand-500/10 flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-6 h-6 text-brand-500" />
          </div>
          <h2 className="text-base font-semibold mb-1">Sign in to continue</h2>
          <p className="text-sm text-muted-foreground mb-4">
            This page is for signed-in members. It only takes a few seconds.
          </p>
          <Button
            onClick={() => openAuth(location.pathname + location.search + location.hash)}
            className="bg-brand-600 hover:bg-brand-700"
            data-testid="protected-route-signin-btn"
          >
            Sign in
          </Button>
        </div>
      </div>
    );
  }

  return children;
}
